import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dropdown,
  Form,
  Grid,
  Icon,
  Message,
  Modal,
} from "semantic-ui-react";
import {
  describeReportFilters,
  resolveReportSelectValue,
} from "./reportFilters";

export const triggerCsvDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const filenameFromDisposition = (value, fallback) => {
  const match = String(value || "").match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
};

const responseErrorMessage = async (error) => {
  const payload = error?.response?.data;
  if (payload instanceof Blob) {
    try {
      const parsed = JSON.parse(await payload.text());
      const message = parsed?.message;
      return Array.isArray(message) ? message.join(" ") : String(message || "");
    } catch (_) {
      // Fall through to the standard actionable copy.
    }
  }
  const message = payload?.message;
  if (message)
    return Array.isArray(message) ? message.join(" ") : String(message);
  return "No pudimos generar el CSV. Revisa los filtros y volve a intentar.";
};

const ReportCsvExportModal = ({
  open,
  onClose,
  title,
  description,
  initialFilters,
  fields,
  normalizeFilters,
  validateFilters,
  requestExport,
  fallbackFilename,
}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [state, setState] = useState("configuring");
  const [feedback, setFeedback] = useState("");
  const [download, setDownload] = useState(null);
  const activeRequestRef = useRef(null);
  const processingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const activeRequest = activeRequestRef.current;
    activeRequestRef.current = null;
    activeRequest?.controller.abort();
    setFilters({ ...initialFilters });
    setState("configuring");
    setFeedback("");
    setDownload(null);
    processingRef.current = false;
  }, [open, initialFilters]);

  useEffect(
    () => () => {
      const activeRequest = activeRequestRef.current;
      activeRequestRef.current = null;
      processingRef.current = false;
      activeRequest?.controller.abort();
    },
    [],
  );

  const close = () => {
    if (processingRef.current) return;
    onClose();
  };

  const cancel = () => {
    const activeRequest = activeRequestRef.current;
    if (!activeRequest) return;
    activeRequestRef.current = null;
    activeRequest.controller.abort();
    processingRef.current = false;
    setState("cancelled");
    setFeedback(
      "La exportacion fue cancelada. Podes ajustar los filtros y volver a intentar.",
    );
  };

  const changeFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    if (state !== "configuring") {
      setState("configuring");
      setFeedback("");
      setDownload(null);
    }
  };

  const submit = async () => {
    if (processingRef.current) return;
    setState("validating");
    setFeedback("Validando filtros...");
    const validationError = validateFilters(filters);
    if (validationError) {
      setState("failure");
      setFeedback(validationError);
      return;
    }

    const normalized = normalizeFilters(filters);
    const controller = new AbortController();
    const activeRequest = { controller };
    activeRequestRef.current = activeRequest;
    processingRef.current = true;
    setState("processing");
    setFeedback(
      "Generando el archivo en Stocky. Esta ventana puede cancelarse sin cambiar el reporte.",
    );

    try {
      const response = await requestExport(normalized, controller.signal);
      if (
        activeRequestRef.current !== activeRequest ||
        controller.signal.aborted
      )
        return;
      const rowCount = Number(response?.headers?.["x-export-row-count"] || 0);
      if (
        response?.status === 204 ||
        !response?.data ||
        response.data.size === 0
      ) {
        setState("empty");
        setFeedback(
          "No hay registros que coincidan con los filtros seleccionados.",
        );
        return;
      }
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const filename = filenameFromDisposition(
        response?.headers?.["content-disposition"],
        fallbackFilename,
      );
      const completed = { blob, filename, rowCount };
      setDownload(completed);
      triggerCsvDownload(blob, filename);
      setState("success");
      setFeedback(
        `Descarga completada${rowCount ? `: ${rowCount} filas` : ""}.`,
      );
    } catch (error) {
      if (activeRequestRef.current !== activeRequest) return;
      if (controller.signal.aborted || error?.code === "ERR_CANCELED") {
        setState("cancelled");
        setFeedback(
          "La exportacion fue cancelada. Podes ajustar los filtros y volver a intentar.",
        );
      } else {
        setState("failure");
        setFeedback(await responseErrorMessage(error));
      }
    } finally {
      if (activeRequestRef.current === activeRequest) {
        activeRequestRef.current = null;
        processingRef.current = false;
      }
    }
  };

  const isProcessing = state === "processing" || state === "validating";
  const summary = describeReportFilters(filters, fields);

  return (
    <Modal open={open} onClose={close} size="small" closeIcon={!isProcessing}>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content>
        <div style={{ display: "grid", gap: 16 }}>
          <p style={{ margin: 0 }}>{description}</p>
          <Message info size="small">
            <Message.Header>Filtros de la exportacion</Message.Header>
            <p>{summary}</p>
            <p style={{ marginBottom: 0 }}>Maximo 10.000 filas.</p>
          </Message>
          <Form>
            <Grid stackable columns={2}>
              {fields.map((field) => (
                <Grid.Column key={field.name}>
                  {field.type === "select" ? (
                    <Form.Field>
                      <label id={`report-export-${field.name}-label`}>
                        {field.label}
                      </label>
                      <Dropdown
                        id={`report-export-${field.name}`}
                        aria-labelledby={`report-export-${field.name}-label`}
                        selection
                        fluid
                        options={field.options}
                        value={resolveReportSelectValue(
                          filters[field.name],
                          field.options,
                        )}
                        disabled={isProcessing}
                        onChange={(_, data) =>
                          changeFilter(field.name, String(data.value || ""))
                        }
                      />
                    </Form.Field>
                  ) : (
                    <Form.Input
                      id={`report-export-${field.name}`}
                      label={field.label}
                      type={field.type || "text"}
                      placeholder={field.placeholder}
                      value={filters[field.name] || ""}
                      disabled={isProcessing}
                      onChange={(event) =>
                        changeFilter(field.name, event.target.value)
                      }
                    />
                  )}
                </Grid.Column>
              ))}
            </Grid>
          </Form>
          {feedback ? (
            <Message
              role="status"
              positive={state === "success"}
              negative={state === "failure"}
              warning={state === "cancelled" || state === "empty"}
              icon
            >
              <Icon
                name={
                  state === "processing" || state === "validating"
                    ? "spinner"
                    : state === "success"
                      ? "check circle"
                      : "info circle"
                }
                loading={state === "processing" || state === "validating"}
              />
              <Message.Content>
                <Message.Header>
                  {state === "success"
                    ? "CSV listo"
                    : state === "empty"
                      ? "Sin resultados"
                      : state === "failure"
                        ? "No se pudo exportar"
                        : state === "cancelled"
                          ? "Exportacion cancelada"
                          : "Procesando"}
                </Message.Header>
                <p>{feedback}</p>
              </Message.Content>
            </Message>
          ) : null}
        </div>
      </Modal.Content>
      <Modal.Actions>
        {isProcessing ? (
          <Button onClick={cancel}>Cancelar exportacion</Button>
        ) : (
          <Button onClick={close}>Cerrar</Button>
        )}
        {download && state === "success" ? (
          <Button
            basic
            color="purple"
            onClick={() => triggerCsvDownload(download.blob, download.filename)}
          >
            Descargar nuevamente
          </Button>
        ) : null}
        {!isProcessing ? (
          <Button
            color="purple"
            onClick={submit}
            disabled={state === "success"}
          >
            {state === "failure" || state === "empty" || state === "cancelled"
              ? "Volver a intentar"
              : "Exportar CSV"}
          </Button>
        ) : null}
      </Modal.Actions>
    </Modal>
  );
};

export default ReportCsvExportModal;
